import { Box, Tooltip, useTheme } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { AiSearchDocumentInfo } from "../../../api/types/chatTypes";

type CitationItemProps = {
    source: AiSearchDocumentInfo;
    onClick: (source: AiSearchDocumentInfo) => void;
    setHoveredPath: Dispatch<SetStateAction<string | null>>;
};

const MAX_WIDTH = 200;

/**
 * CitationItem Component
 *
 * Displays a single citation path with styling and ellipsis if it overflows.
 * When the citation text is truncated due to overflow, a tooltip with the full
 * citation is shown on hover. Also updates the hovered path state on mouse events.
 *
 * @component
 * @param {CitationItemProps} props - The props for the CitationItem.
 * @returns {JSX.Element} A styled citation element, optionally wrapped in a tooltip if it overflows.
 *
 * @example
 * ```tsx
 * <CitationItem
 *   path="/example_1-2.pdf"
 *   onClick={(filePath) => console.log(filePath)}
 *   setHoveredPath={setHoveredPath}
 * />
 * ```
 */
function CitationItem({ source, onClick, setHoveredPath }: CitationItemProps) {
    const theme = useTheme();
    const formattedCitation = source.citation_display_name || "";
    const textRef = useRef<HTMLSpanElement>(null);
    const [isOverflowed, setIsOverflowed] = useState(false);

    useEffect(() => {
        const textElement = textRef.current;
        if (textElement) setIsOverflowed(textElement.scrollWidth >= MAX_WIDTH);
    }, [source]);

    const citation = (
        <Box
            ref={textRef}
            sx={{
                color: theme.palette.text.primary,
                fontSize: "0.9375rem",
                fontWeight: "600",
                maxWidth: `${MAX_WIDTH}px`,
                overflow: "hidden",
                textAlign: "start",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
                textDecoration: "none",
                opacity: 0.4,
                padding: "5px 8px",
                lineHeight: 1.5,
                "&:hover": { opacity: 0.8 }
            }}
            onMouseEnter={() => setHoveredPath(source.citation_display_name)}
            onMouseLeave={() => setHoveredPath(null)}
            onClick={() => onClick(source)}
        >
            {formattedCitation}
        </Box>
    );

    return isOverflowed ? (
        <Tooltip title={formattedCitation} arrow>
            {citation}
        </Tooltip>
    ) : (
        citation
    );
}

export default CitationItem;
